import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { formatDate } from '../helpers/date';
import { NOTIFICATION_TYPE_LABELS, NOTIFICATION_TYPE_SUBTITLES } from '../constants/notification';

const normalizeNotification = (item) => {
  const type = item?.type;
  const title = (type && NOTIFICATION_TYPE_LABELS[type]) || 'Notification';
  const actor = item?.actor ?? {};
  const actorName = actor.name || 'Utilisateur';
  const subtitleFn = type ? NOTIFICATION_TYPE_SUBTITLES[type] : null;
  const description =
    (subtitleFn && typeof subtitleFn === 'function' ? subtitleFn(actorName) : null) ?? '';
  const createdAt = item?.createdAt;
  const dateStr = createdAt ? formatDate(createdAt, { format: 'MMM D' }) : '';

  return {
    id: item?.id,
    type,
    title,
    description,
    date: dateStr,
    createdAt: createdAt,
    unread: !item?.seen,
    actorId: actor?.id,
    entityId: item?.entityId,
  };
};

const PAGE_SIZE = 20;

const parseUnreadCount = (response) => {
  const data = response?.data?.data ?? response?.data ?? response;
  if (typeof data?.count === 'number') return data.count;
  if (typeof data === 'number') return data;
  return 0;
};

export const useNotifications = (filter = 'all') => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const abortControllerRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    const response = await api.get('/notifications/unread-count');
    if (response?.ok) {
      setUnreadCount(parseUnreadCount(response));
    }
  }, []);

  const fetchInitial = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setPage(1);
    setHasMore(true);
    const params = {
      page: 1,
      limit: PAGE_SIZE,
      ...(filter === 'unread' ? { seen: false } : {}),
    };
    const response = await api.get('/notifications', { params, signal });

    if (signal.aborted || response?.error?.code === 'ERR_CANCELED') return;
    abortControllerRef.current = null;

    if (!response?.ok) {
      setLoading(false);
      return;
    }

    const data = response.data?.data ?? response.data ?? [];
    const list = Array.isArray(data) ? data : [];
    const normalized = list.map(normalizeNotification);
    const seen = new Set();
    setNotifications(
      normalized.filter((n) => {
        if (n.id == null || seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      })
    );
    setHasMore(list.length >= PAGE_SIZE);
    setLoading(false);
    fetchUnreadCount();
  }, [filter, fetchUnreadCount]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    const params = {
      page: nextPage,
      limit: PAGE_SIZE,
      ...(filter === 'unread' ? { seen: false } : {}),
    };
    const response = await api.get('/notifications', { params });

    if (!response?.ok) {
      setLoadingMore(false);
      return;
    }

    const data = response.data?.data ?? response.data ?? [];
    const list = Array.isArray(data) ? data : [];
    const normalized = list.map(normalizeNotification);
    setNotifications((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const newItems = normalized.filter((n) => n.id != null && !existingIds.has(n.id));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });
    setPage(nextPage);
    setHasMore(list.length >= PAGE_SIZE);
    setLoadingMore(false);
  }, [filter, page, loading, loadingMore, hasMore]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    fetchInitial();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchInitial]);

  const markAsRead = useCallback(
    async (id) => {
      const response = await api.patch(`/notifications/${id}/read`);
      if (response?.ok) {
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
        fetchUnreadCount();
      }
    },
    [fetchUnreadCount]
  );

  const markAllAsRead = useCallback(async () => {
    const response = await api.patch('/notifications/mark-all-read');
    if (response?.ok) {
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  return {
    data: notifications,
    unreadCount,
    fetchUnreadCount,
    loading,
    loadingMore,
    hasMore,
    refetch: fetchInitial,
    loadMore,
    markAsRead,
    markAllAsRead,
  };
};
